import Button from '@rio-cloud/rio-uikit/Button';
import NotFoundState from '@rio-cloud/rio-uikit/components/states/NotFoundState';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import LabeledElement from '@rio-cloud/rio-uikit/LabeledElement';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { useIntl } from 'react-intl';

import { useDownloadLinkQuery } from '#api/documentLinkApi.slice.ts';
import type { Document } from '#domain/Document.ts';

import { RioImage } from './RioImage.tsx';

type CreateOrderDialogProps = {
    doc: Document | null;
    onClose: () => void;
};

export const DocumentDialog = ({ doc, onClose }: CreateOrderDialogProps) => {
    const { data } = useDownloadLinkQuery(
        { documentId: doc?.externalId ?? doc?.id ?? '', fileName: doc?.fileName ?? '' },
        { skip: !doc }
    );
    const intl = useIntl();
    const dialogTitleId = 'intl-msg:ordercom-cloud-frontend.order.documents.header';

    const imageSrc = data?.url;

    const isImage = ['.png', '.jpeg', '.jpg', '.webp'].some(ext => doc?.fileName?.endsWith(ext));
    // RIOAPP-2782 Fix broken fileName for Driver_protocol of Pocket Driver Apps with version 3.0.27
    const isPDF = ['.pdf'].some(ext => doc?.fileName?.endsWith(ext)) || doc?.fileName === 'Driver_protocol';

    const handleOpenInNewTab = () => {
        if (isImage || isPDF) {
            window.open(imageSrc, '_blank', 'noopener,noreferrer');
        }
    };

    const handleDownload = () => {
        if (!doc) {
            return;
        }
        if (isImage || isPDF) {
            const link = document.createElement('a');
            if (!imageSrc) {
                throw Error('Failed to create download link because there is not image');
            }
            link.href = imageSrc;
            link.setAttribute('download', doc.fileName);
            link.setAttribute('target', '_blank');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const relatedOrderId = doc?.associations?.find(ac => ac.type === 'transport-order')?.id;

    const DialogBody = () => {
        if (!imageSrc) {
            return <Spinner isInverse={false} />;
        }
        if (!doc) {
            return null;
        }
        return (
            <div className='display-flex-sm gap-20'>
                <div className='width-500 border rounded display-flex align-items-center overflow-hidden'>
                    {isImage && <RioImage imageSrc={imageSrc} />}
                    {isPDF && <embed src={imageSrc} type='application/pdf' className='width-100pct min-height-500' />}
                    {!(isImage || isPDF) && (
                        <NotFoundState
                            headline={intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.order.documents.not_found',
                            })}
                            condensed
                            outerClassName='border-none'
                        />
                    )}
                </div>
                <div className='min-width-200 max-width-200 padding-top-10'>
                    <div className='display-flex flex-column gap-50 height-100pct'>
                        <div className='flex-1-1'>
                            <div className='text-medium text-size-18 line-height-125rel'>{doc.type}</div>
                            <div>{doc.name}</div>

                            <div className='display-flex flex-column gap-15 margin-top-20'>
                                <LabeledElement
                                    label={intl.formatMessage({
                                        id: 'intl-msg:ordercom-cloud-frontend.order.documents.filename',
                                    })}
                                    noGap
                                >
                                    <div className='display-flex gap-3 align-items-start'>
                                        <span className='uikit-icon icon-document text-color-dark margin-top-3' />
                                        <div className='word-break-all line-height-125rel'>{doc.fileName}</div>
                                    </div>
                                </LabeledElement>
                                {relatedOrderId && (
                                    <LabeledElement label='Dazugehöriger Auftrag' noGap>
                                        <div className='display-flex gap-3 align-items-start'>
                                            <span className='uikit-icon icon-hash text-color-dark margin-top-3' />
                                            <div className='word-break-all line-height-125rel'>{relatedOrderId}</div>
                                        </div>
                                    </LabeledElement>
                                )}
                            </div>
                        </div>

                        <div className='btn-group'>
                            <Button
                                className='flex-1-1-0'
                                bsStyle='muted-filled'
                                iconName='new-window'
                                iconOnly
                                autoTrackingKey='ordercom.documents.dialog.open-new-window'
                                onClick={handleOpenInNewTab}
                            />
                            <Button
                                className='flex-1-1-0'
                                bsStyle='muted-filled'
                                iconName='download'
                                iconOnly
                                autoTrackingKey='ordercom.documents.dialog.download'
                                onClick={handleDownload}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Dialog
            show={!!doc}
            onClose={onClose}
            showCloseButton={false}
            bsSize='md'
            data-testid={`order-dialog-${doc?.name ?? 'none'}`}
            title={intl.formatMessage({
                id: dialogTitleId,
            })}
            body={<DialogBody />}
            headerButtons={
                <Button
                    bsStyle={Button.MUTED}
                    iconOnly
                    iconName='remove'
                    autoTrackingKey={`${dialogTitleId}.close`}
                    className='modal-header-close close'
                    onClick={onClose}
                    aria-label='dialog close button'
                />
            }
        />
    );
};
